/* eslint-disable  no-underscore-dangle */

import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import Loadable from 'react-loadable';

import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import configureStore, { history, bootstrapSaga } from './configureStore';
import Router from './route';
import { AbilityProvider } from './rbac';

import './global.scss';
import './lib/firebase';

// Defuse the benign "ResizeObserver loop ..." warning at its source. It fires
// when a ResizeObserver callback (Google Maps installs one on its map container,
// Ant's dropdowns use rc-resize-observer) schedules a layout change that triggers
// another resize within the same frame — e.g. opening the status dropdown resizes
// the map, whose observer re-lays-out, and so on. Nothing is actually broken, but
// CRA's dev overlay treats the resulting error event as fatal and throws up the
// red screen. Wrapping the callback in requestAnimationFrame defers it to the next
// frame, breaking the synchronous loop so the browser never emits the warning.
// Must run before any ResizeObserver is constructed (i.e. before components mount).
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class ResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

// Backstop: if the loop warning still slips through (e.g. an observer created
// before the patch), swallow ONLY this exact message so it can't trip the dev
// overlay. Everything else propagates normally.
const RESIZE_OBSERVER_ERR = /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener(
  'error',
  (e) => {
    if (e.message && RESIZE_OBSERVER_ERR.test(e.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true // capture phase — run before bubble-phase listeners like the dev overlay's
);

// Lazy-load the Google Maps wrapper so its ~100 KB raw of components only ships
// to non-inspection routes. /inspection bypasses this wrapper entirely below
// and never fetches the chunk. LoadScript renders its own Loader once mounted,
// so we use a transparent fallback here to avoid a double-loader flash.
const MapsProvider = Loadable({
  loader: () => import(/* webpackChunkName: "maps-provider" */ './components/MapsProvider'),
  loading: () => null,
  delay: false,
});

const env = window._env_.DEPLOYMENT_PROFILE;

let GKey = process.env.REACT_APP_PROD_GOOGLEAPIKEY;

if (env === 'dev' || env === 'preDev') {
  GKey = process.env.REACT_APP_DEV_GOOGLEAPIKEY;
}

let sentryDsn = process.env.REACT_APP_PROD_SENTRY_DSN;

const isDev = env === 'dev' || env === 'preDev';

if (isDev) {
  sentryDsn = process.env.REACT_APP_DEV_SENTRY_DSN;
}

const target = document.querySelector('#root');
const store = configureStore();

// /inspection is a deep-linked mobile webview hosted by the native mobile app,
// which has its own crash reporting. Skip <LoadScript>, Sentry, AND saga
// registration on that route — the inspection page calls Heroku via direct
// fetch (services/inspection.js), dispatches no saga-handled actions, and
// reads no auth state. Skipping bootstrapSaga() avoids fetching the sagas
// chunk + its transitive admin-service deps (~150 KB gz) on /inspection.
const isInspection = window.location.pathname.startsWith('/inspection');

if (!isInspection) {
  bootstrapSaga();
}

// Lazy-load Sentry on non-inspection routes. Init is the only Sentry call site
// in the app, so the brief window before the chunk resolves only widens the gap
// during which automatic error capture isn't installed (errors still surface
// in the browser console; they just aren't Sentry-tracked).
if (!isInspection) {
  import(/* webpackChunkName: "sentry" */ '@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      environment: env,
      sendDefaultPii: isDev,
    });
  });
}

ReactDOM.render(
  <Provider store={store}>
    <AbilityProvider>
      <BrowserRouter>
        {isInspection ? (
          <Router history={history} />
        ) : (
          <MapsProvider apiKey={GKey}>
            <Router history={history} />
          </MapsProvider>
        )}
      </BrowserRouter>
    </AbilityProvider>
  </Provider>,
  target
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorkerRegistration.unregister();

// Being used in MainLayout redux
export { store, history };
